import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Feather, Hand, ShieldCheck, Sparkles, Truck, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import V2ProductCard from "@/v2/ProductCard";
import { usePageImages } from "@/v2/lib/usePageImages";

const HERO_IMAGE = "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=2000&q=85";

const CATEGORIES = [
  { slug: "carpets", name: "Hand-Tufted Rugs", img: "https://m.media-amazon.com/images/I/71ltZ6LOmqL._SL1200_.jpg" },
  { slug: "carpets", name: "Runner Rugs", img: "https://m.media-amazon.com/images/I/81hRSTAOZoL._SL1200_.jpg" },
  { slug: "rugs", name: "Jute Rugs", img: "https://m.media-amazon.com/images/I/710mGeGqLvL._SL1200_.jpg" },
  { slug: "custom", name: "Custom Rugs", img: "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=1200&q=85", isCustom: true },
  { slug: "photo-frames", name: "Home Décor", img: "https://m.media-amazon.com/images/I/71YogLlzF7L._SL1200_.jpg" },
];

const COLLECTIONS = [
  { name: "Modern Expressions", copy: "Bold lines. Considered colour. Rugs that make a room think.", img: "https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=1400&q=85", slug: "modern" },
  { name: "Timeless Neutrals", copy: "Warm ivories, soft sands, quiet oats — the everyday classics.", img: "https://images.unsplash.com/photo-1705326701287-346fc37a2c86?auto=format&fit=crop&w=1400&q=85", slug: "neutrals" },
  { name: "The Runner Edit", copy: "Long, narrow, patient — anchoring passageways and kitchens.", img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1400&q=85", slug: "runners" },
  { name: "Natural Jute", copy: "Undyed, hand-braided, kind to bare feet and quiet living.", img: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?auto=format&fit=crop&w=1400&q=85", slug: "jute" },
];

const ROOMS = [
  { name: "Living Room", img: "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=1200&q=85" },
  { name: "Bedroom", img: "https://images.unsplash.com/photo-1705326701287-346fc37a2c86?auto=format&fit=crop&w=1200&q=85" },
  { name: "Dining Room", img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=85" },
  { name: "Entryway", img: "https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=1200&q=85" },
  { name: "Kids' Room", img: "https://images.unsplash.com/photo-1610384104075-e05c8cf200c3?auto=format&fit=crop&w=1200&q=85" },
];

const REVIEWS = [
  { quote: "The rug feels like it has always belonged in our home. Every knot has care in it.", by: "Anaïs V.", place: "Paris" },
  { quote: "The runner arrived with a handwritten note from the maker. Unheard of at this price.", by: "Meera S.", place: "Bengaluru" },
  { quote: "Our custom rug turned a childhood sketch into an heirloom. Our daughter cried.", by: "Rohan & Priya", place: "Mumbai" },
];

const JOURNAL = [
  { title: "How to Choose the Right Rug Size", cat: "Guides", img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=900&q=85" },
  { title: "Caring for a Hand-Tufted Rug", cat: "Craft", img: "https://images.pexels.com/photos/29848182/pexels-photo-29848182.jpeg?auto=compress&cs=tinysrgb&w=900" },
  { title: "Rugs for Homes with Children", cat: "Living", img: "https://images.unsplash.com/photo-1610384104075-e05c8cf200c3?auto=format&fit=crop&w=900&q=85" },
];

export default function V2Home() {
  const [products, setProducts] = useState([]);
  const { img, images } = usePageImages();
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || []));
  }, []);
  const featured = products.slice(0, 4);

  // Build hero image set — honour mobile override when provided.
  const heroDesktop = img("home.hero.desktop", HERO_IMAGE);
  const heroMobile = img("home.hero.mobile", images["home.hero.mobile"] || heroDesktop);

  return (
    <div data-testid="v2-home-page">
      {/* 1. HERO */}
      <section className="relative h-[90vh] min-h-[600px] max-h-[880px] w-full overflow-hidden">
        <picture>
          <source media="(max-width: 767px)" srcSet={heroMobile} />
          <img src={heroDesktop} alt="Handcrafted rug lifestyle" className="absolute inset-0 w-full h-full object-cover" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/80 via-[#1a1a1a]/40 to-[#1a1a1a]/25" />
        <div className="relative h-full max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-16 md:pb-24 text-[#fbf9f5]">
          <div className="overline-v2 text-[#c9ac93] v2-fade">Handcrafted in India</div>
          <h1 className="v2-fade v2-fade-1 mt-5 font-serif-warm text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tight max-w-4xl">
            Handcrafted Stories<br />for Your Home.
          </h1>
          <p className="v2-fade v2-fade-2 mt-8 max-w-xl text-[16px] md:text-[17px] text-white/85 font-light leading-relaxed">
            Discover thoughtfully designed rugs and décor, handmade by skilled Indian artisans to bring warmth, texture and character into your space.
          </p>
          <div className="v2-fade v2-fade-3 mt-10 flex flex-wrap gap-4">
            <Link to="/collections?cat=carpets" data-testid="v2-hero-explore" className="v2-btn-primary bg-[#fbf9f5] text-[#1a1a1a] border-[#fbf9f5] hover:bg-[#b85742] hover:text-[#fbf9f5] hover:border-[#b85742]">
              Explore Rugs <ArrowRight size={14} />
            </Link>
            <Link to="/custom" data-testid="v2-hero-custom" className="v2-btn-outline text-[#fbf9f5] border-[#fbf9f5] hover:bg-[#fbf9f5] hover:text-[#1a1a1a]">
              Design Your Rug
            </Link>
          </div>
        </div>
      </section>

      {/* 2. SHOP BY CATEGORY */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="overline-v2">Explore the atelier</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              Five crafts, one considered home.
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.name}
              to={c.isCustom ? "/custom" : `/collections?cat=${c.slug}`}
              data-testid={`v2-category-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={`group block ${i === 3 && !c.isCustom ? "" : ""}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-[#f2ede2]">
                <img src={img(`home.crafts.${i}`, c.img)} alt={c.name} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/50 via-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end text-[#fbf9f5]">
                  <div className="font-serif-warm text-lg md:text-xl leading-tight">{c.name}</div>
                  <ArrowUpRight size={16} className="transition-transform group-hover:rotate-45" />
                </div>
              </div>
              <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-[#4a4640]">Explore →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* 3. FEATURED COLLECTIONS */}
      <section className="bg-[#f2ede2] py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="mb-14">
            <div className="overline-v2">Featured collections</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
              Small edits, considered together.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {COLLECTIONS.map((c, i) => (
              <Link key={c.name} to={`/collections?collection=${c.slug}`} data-testid={`v2-collection-${c.slug}`} className={`group block ${i % 2 === 1 ? "md:mt-12" : ""}`}>
                <div className="relative aspect-[5/6] overflow-hidden bg-[#e7dfd0]">
                  <img src={img(`home.edits.${i}`, c.img)} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1000ms] group-hover:scale-[1.04]" />
                </div>
                <div className="mt-5 flex justify-between items-start gap-6">
                  <div>
                    <div className="overline-v2">Collection · 0{i + 1}</div>
                    <h3 className="mt-2 font-serif-warm text-3xl md:text-4xl text-[#1a1a1a]">{c.name}</h3>
                    <p className="mt-3 text-[14px] text-[#4a4640] max-w-md font-light leading-relaxed">{c.copy}</p>
                  </div>
                  <ArrowUpRight size={20} className="mt-2 transition-transform group-hover:rotate-45 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. FEATURED PRODUCTS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="overline-v2">This season</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-2xl">
              Bestsellers, quietly loved.
            </h2>
          </div>
          <Link to="/collections" className="v2-link text-[12px] uppercase tracking-[0.24em] text-[#1a1a1a] pb-1">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.map((p) => <V2ProductCard key={p.id} product={p} testIdPrefix="v2-home-featured" />)}
        </div>
      </section>

      {/* 5. CRAFTSMANSHIP STORY */}
      <section className="bg-[#1a1a1a] text-[#fbf9f5]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline-v2 text-[#c9ac93]">The Iryaa Craft</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Made by hand.<br /><em>Made to belong.</em>
            </h2>
            <p className="mt-8 text-[15px] text-white/75 leading-relaxed font-light max-w-md">
              Every Iryaa Homes rug carries the touch of skilled hands. From selecting yarns and developing colours to tufting, finishing and final inspection, each piece is created with care by artisans who have inherited generations of craftsmanship.
            </p>
            <Link to="/about" className="mt-10 v2-btn-outline text-[#fbf9f5] border-[#fbf9f5] hover:bg-[#fbf9f5] hover:text-[#1a1a1a]">
              Discover Our Craft <ArrowRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
            <img src={img("home.craftsmanship.0", "https://images.pexels.com/photos/29848182/pexels-photo-29848182.jpeg?auto=compress&cs=tinysrgb&w=900")} alt="Artisan at work" className="w-full aspect-[3/4] object-cover" />
            <img src={img("home.craftsmanship.1", "https://images.pexels.com/photos/37877827/pexels-photo-37877827.jpeg?auto=compress&cs=tinysrgb&w=900")} alt="Detailing" className="w-full aspect-[3/4] object-cover mt-12 md:mt-16" />
          </div>
        </div>
      </section>

      {/* 6. CUSTOM RUG */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <img src={img("home.custom_side", "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=1400&q=85")} alt="Custom rug" className="w-full aspect-[4/5] object-cover" />
        </div>
        <div className="lg:col-span-6 lg:pl-8">
          <div className="overline-v2">The Custom Studio</div>
          <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-xl">
            A rug created for your space.
          </h2>
          <p className="mt-8 text-[16px] text-[#4a4640] leading-relaxed font-light max-w-lg">
            Choose your size, colours and design, or share an inspiration image with us. Our team will help transform your idea into a rug handcrafted especially for your home.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/custom" data-testid="v2-custom-cta" className="v2-btn-primary">
              Start Your Custom Rug <ArrowRight size={14} />
            </Link>
            <Link to="/designer" className="v2-btn-outline">
              <Sparkles size={13} /> AI-assisted preview
            </Link>
          </div>
        </div>
      </section>

      {/* 7. SHOP BY ROOM */}
      <section className="bg-[#f2ede2] py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="mb-12">
            <div className="overline-v2">Shop by Room</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Rugs in real rooms.</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {ROOMS.map((r, i) => (
              <Link key={r.name} to={`/collections?room=${encodeURIComponent(r.name.toLowerCase())}`} className="group block">
                <div className="relative aspect-[3/4] overflow-hidden bg-[#e7dfd0]">
                  <img src={img(`home.rooms.${i}`, r.img)} alt={r.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[#1a1a1a]/10 group-hover:bg-[#1a1a1a]/30 transition-colors" />
                  <div className="absolute inset-0 flex items-end p-4">
                    <div className="font-serif-warm text-lg md:text-xl text-[#fbf9f5]">{r.name}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 8. WHY IRYAA */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="overline-v2">Why Iryaa Homes</div>
          <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Quiet promises we keep.</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-6">
          {[
            { icon: Hand, t: "Handmade in India" },
            { icon: Sparkles, t: "Skilled artisans" },
            { icon: Feather, t: "Custom sizes" },
            { icon: ShieldCheck, t: "Quality checked" },
            { icon: Truck, t: "Secure delivery" },
            { icon: MessageCircle, t: "Personal support" },
          ].map((f) => (
            <div key={f.t} className="text-center">
              <div className="mx-auto w-12 h-12 border border-[#c9c0ae] rounded-full flex items-center justify-center text-[#b85742]">
                <f.icon size={18} strokeWidth={1.4} />
              </div>
              <div className="mt-4 text-[13px] text-[#1a1a1a] font-medium leading-tight">{f.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* 9. FOUNDER STORY */}
      <section className="bg-[#f2ede2]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <img src={img("home.founder", "https://images.pexels.com/photos/37877827/pexels-photo-37877827.jpeg?auto=compress&cs=tinysrgb&w=1200")} alt="Founder" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <div className="overline-v2">Founder</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              A homegrown brand<br />with a heart for handmade.
            </h2>
            <p className="mt-8 text-[16px] text-[#4a4640] leading-relaxed font-light max-w-xl">
              Founded by Alka, a mother of two with a deep love for meaningful interiors, Iryaa Homes was created to bring beautifully handcrafted products into modern homes while celebrating the artisans behind every piece.
            </p>
            <Link to="/about" className="mt-10 inline-flex v2-btn-outline">Our Story <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* 10. REVIEWS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="mb-14">
          <div className="overline-v2">In their homes</div>
          <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">Quietly loved, one home at a time.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {REVIEWS.map((r, i) => (
            <figure key={i} className="border-t border-[#1a1a1a] pt-6">
              <div className="text-[#b85742] text-lg">★★★★★</div>
              <blockquote className="mt-4 font-serif-warm text-2xl leading-snug text-[#1a1a1a]">"{r.quote}"</blockquote>
              <figcaption className="mt-6 text-[12px] uppercase tracking-[0.22em] text-[#7a7466]">— {r.by} · {r.place}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 11. JOURNAL */}
      <section className="bg-[#f2ede2] py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="overline-v2">The Iryaa Journal</div>
              <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl lg:text-6xl leading-[1.05]">Stories from Iryaa.</h2>
            </div>
            <Link to="/journal" className="v2-link text-[12px] uppercase tracking-[0.24em] text-[#1a1a1a] pb-1">Read the journal →</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {JOURNAL.map((a, i) => (
              <Link key={a.title} to="/journal" className="group block">
                <div className="relative aspect-[4/5] overflow-hidden bg-[#e7dfd0]">
                  <img src={img(`home.journal.${i}`, a.img)} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                </div>
                <div className="mt-4">
                  <div className="overline-v2 text-[10px]">{a.cat}</div>
                  <div className="mt-2 font-serif-warm text-2xl text-[#1a1a1a] leading-tight">{a.title}</div>
                  <div className="mt-3 text-[12px] uppercase tracking-[0.2em] text-[#7a7466]">Read →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 12. INSTAGRAM */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-center mb-12">
          <div className="overline-v2">Follow along</div>
          <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl">@iryaa_homes on Instagram</h2>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-3">
          {[
            "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=500&q=80",
            "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=500&q=80",
            "https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=500&q=80",
            "https://images.unsplash.com/photo-1705326701287-346fc37a2c86?auto=format&fit=crop&w=500&q=80",
            "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=500&q=80",
            "https://images.unsplash.com/photo-1610384104075-e05c8cf200c3?auto=format&fit=crop&w=500&q=80",
          ].map((src, i) => (
            <a key={i} href="https://instagram.com/iryaa_homes" target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden bg-[#e7dfd0] group">
              <img src={img(`home.instagram.${i}`, src)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-110" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
